import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { submitTakeItDownReport } from '@genfun/core/api/API';
import { SITE } from '../data/site';
import { Icon } from '../components/Icon';
import { LegalLayout } from './Legal';

export function TakeItDownReport() {
    const [params] = useSearchParams();
    const mediaId = params.get('media_id') || '';
    const hasMediaId = !!mediaId;

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [url, setUrl] = useState('');
    const [description, setDescription] = useState('');
    const [confirmed, setConfirmed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);

    const targetFilled = hasMediaId ? true : !!url.trim();
    const canSubmit = name.trim() && email.trim() && targetFilled && description.trim() && confirmed && !submitting;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setError(null);
        setSubmitting(true);
        try {
            const [data, status] = await submitTakeItDownReport({
                name: name.trim(),
                email: email.trim(),
                description: description.trim(),
                ...(hasMediaId ? { media_id: mediaId } : { url: url.trim() }),
            });
            if (status >= 200 && status < 300) setSubmitted(true);
            else if (status === 429) setError('Too many requests. Please try again in a few minutes.');
            else if (status === 400 && data?.detail) setError(data.detail);
            else setError('Could not submit the request. Please try again later.');
        } catch {
            setError('Network error. Please check your connection and try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <LegalLayout title="Content removal request" eyebrow="Take it down" lastUpdated={null}>
            <section>
                <p className="legalIntro">
                    Under the TAKE IT DOWN Act (TIDA), you may request removal of nonconsensual intimate visual
                    depictions. We will process your request within 48 hours.
                </p>
            </section>

            {submitted ? (
                <div className="tidaDone">
                    <span className="vidCreateIcon"><Icon name="check" size={26} strokeWidth={2.6} /></span>
                    <h2>Request received</h2>
                    <p>
                        Thanks — your removal request has been sent to our team. We will review it
                        and respond within 48 hours at the email address you provided.
                    </p>
                    <Link className="btn btn--glass" to="/"><Icon name="arrowLeft" size={15} /> Back to the site</Link>
                </div>
            ) : (
                <form className="tidaForm" onSubmit={onSubmit} noValidate>
                    <label>
                        <span className="fieldLabel">Your full name</span>
                        <input type="text" className="field" placeholder="Jane Smith" value={name} onChange={(e) => setName(e.target.value)} required />
                    </label>

                    <label>
                        <span className="fieldLabel">Email address</span>
                        <input type="email" className="field" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
                    </label>

                    {hasMediaId ? (
                        <div>
                            <span className="fieldLabel">Reporting gallery item</span>
                            <div className="tidaPinned">
                                <span className="badge badge--new">media_id</span>
                                <code>{mediaId}</code>
                            </div>
                            <span className="tidaHelp">Selected from the gallery — the team will look up this item directly.</span>
                        </div>
                    ) : (
                        <label>
                            <span className="fieldLabel">URL(s) of the content to be removed</span>
                            <textarea className="field" placeholder={`https://${SITE.domain}/...`} value={url} onChange={(e) => setUrl(e.target.value)} rows={4} required />
                            <span className="tidaHelp">Paste one URL per line</span>
                        </label>
                    )}

                    <label>
                        <span className="fieldLabel">Describe the content and why you are requesting removal</span>
                        <textarea
                            className="field"
                            placeholder="Please describe the content and explain why it should be removed..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={6}
                            required
                        />
                    </label>

                    <label className="tidaConfirm">
                        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
                        <span className="tidaCheck" aria-hidden="true"><Icon name="check" size={13} strokeWidth={3.2} /></span>
                        <span>
                            I confirm that the information provided is accurate and that I am the
                            person depicted or their authorized representative.
                        </span>
                    </label>

                    {error && <div className="note note--error">{error}</div>}

                    <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={!canSubmit}>
                        {submitting ? 'Submitting…' : 'Submit removal request'}
                    </button>

                    <p className="tidaHelp tidaFoot">
                        We will respond within 48 hours · Questions?{' '}
                        <a href={`mailto:${SITE.emails.support}`}>{SITE.emails.support}</a>
                    </p>
                </form>
            )}
        </LegalLayout>
    );
}
